import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, MapPin, Truck, TrendingUp, TrendingDown, Calendar, Loader,
  AlertCircle, Package, DollarSign, Route, BarChart3,
  Users, Target, Zap, Lightbulb, Clock, Radio
} from 'lucide-react';
import PeriodFilter from './PeriodFilter';
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

interface MonthRange {
  startMonth: number;
  endMonth: number;
}

interface LogisticsNetworkModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
  onPeriodChange?: (year: number, monthRanges: MonthRange[]) => void;
  isLoading?: boolean;
}

const LogisticsNetworkModal: React.FC<LogisticsNetworkModalProps> = ({
  isOpen,
  onClose,
  data,
  onPeriodChange,
  isLoading = false
}) => {
  const [showPeriodFilter, setShowPeriodFilter] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState<number | undefined>(undefined);

  const handlePeriodChange = (year: number, monthRanges: MonthRange[]) => {
    if (onPeriodChange) {
      onPeriodChange(year, monthRanges);
    }
  };

  // Extrair dados da Força de Venda para contexto logístico
  const salesData = data || {};
  const kpis = salesData.kpis || {};
  const product_distribution = salesData.product_distribution || {};
  const geographic_distribution = salesData.geographic_distribution || {};
  const vehicle_analysis = salesData.vehicle_cost_analysis || {};
  const insights = salesData.insights || [];
  const client_segmentation = salesData.client_segmentation || {};

  const formatNumber = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toFixed(0);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // KPI Card Component
  const KPICard = ({ icon: Icon, title, value, subtitle, color, bgColor }: any) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
      style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-4 rounded-xl bg-gradient-to-br shadow-lg`} style={{ background: color }}>
          <Icon className="w-8 h-8 text-white" />
        </div>
        <TrendingUp className="w-5 h-5 text-green-500" />
      </div>
      <div className="text-4xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
        {value}
      </div>
      <div className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
        {title}
      </div>
      <div className="mt-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
        {subtitle}
      </div>
    </motion.div>
  );

  // Detail Card Component
  const DetailCard = ({ icon: Icon, title, items, color }: any) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 rounded-2xl shadow-lg"
      style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
    >
      <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
        <Icon className="w-5 h-5" style={{ color }} />
        {title}
      </h3>
      <div className="space-y-3">
        {items.map((item: any, idx: number) => (
          <div key={idx} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
            <div className="flex items-center gap-2">
              {item.emoji && <span className="text-xl">{item.emoji}</span>}
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                {item.label}
              </span>
            </div>
            <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
      {items.length > 0 && (
        <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-primary)' }}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Total</span>
            <span className="text-lg font-bold" style={{ color: 'var(--primary)' }}>
              {items.length} itens
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );

  // Insight Card Component
  const InsightCard = ({ title, description, icon: Icon, color }: any) => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="p-5 rounded-xl border-l-4"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderLeftColor: color,
      }}
    >
      <div className="flex items-start gap-3">
        <div className="p-3 rounded-lg mt-1" style={{ backgroundColor: color + '20' }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
        <div className="flex-1">
          <p className="font-bold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
            {title}
          </p>
          <p className="text-xs opacity-70 leading-relaxed">{description}</p>
        </div>
      </div>
    </motion.div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="border-2 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
              style={{
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-primary)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                className="px-8 py-6 border-b"
                style={{ borderColor: 'var(--border-primary)' }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: 'var(--bg-tertiary)' }}
                    >
                      <MapPin className="h-6 w-6" style={{ color: '#3B82F6' }} />
                    </div>
                    <div>
                      <h2
                        className="text-3xl font-bold"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        Malha Logística
                      </h2>
                      <p
                        className="text-sm mt-1"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        Resumo de Operações Logísticas e Análise de Força de Venda
                      </p>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={onClose}
                    className="p-2 rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--bg-tertiary)' }}
                  >
                    <X className="h-6 w-6" style={{ color: 'var(--text-secondary)' }} />
                  </motion.button>
                </div>

                {/* Controles */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <span className="text-sm opacity-60">Dados da Análise de Força de Venda</span>
                  {onPeriodChange && (
                    <button
                      onClick={() => setShowPeriodFilter(!showPeriodFilter)}
                      className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      <Calendar className="h-4 w-4" />
                      <span>{showPeriodFilter ? 'Fechar' : 'Alterar'} Período</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Period Filter */}
              {showPeriodFilter && onPeriodChange && (
                <div className="px-8 py-4 border-b" style={{ borderColor: 'var(--border-primary)' }}>
                  <PeriodFilter onFilter={handlePeriodChange} />
                </div>
              )}

              {/* Loading */}
              {isLoading && (
                <div className="flex items-center justify-center p-16">
                  <div className="text-center">
                    <Loader className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: 'var(--primary)' }} />
                    <p style={{ color: 'var(--text-secondary)' }}>
                      Analisando dados com IA...
                    </p>
                  </div>
                </div>
              )}

              {/* Content */}
              {!isLoading && (
                <div className="px-8 py-8 overflow-y-auto max-h-[calc(90vh-200px)] space-y-8">
                  {/* Cobertura Geográfica - No topo */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-8 rounded-2xl shadow-lg"
                    style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                  >
                    <h3 className="text-2xl font-bold mb-8 flex items-center gap-3" style={{ color: 'var(--text-primary)' }}>
                      <MapPin className="w-7 h-7" style={{ color: '#3B82F6' }} />
                      Cobertura Geográfica
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {/* Regiões Atendidas */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="p-8 rounded-xl text-center"
                        style={{
                          background: 'linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)',
                          boxShadow: '0 4px 6px rgba(59, 130, 246, 0.3)'
                        }}
                      >
                        <div className="text-5xl font-bold mb-2 text-white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                          {geographic_distribution.top_regions ? geographic_distribution.top_regions.length : 0}
                        </div>
                        <div className="text-sm font-bold text-white mb-3" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Regiões Atendidas</div>
                        <p className="text-xs text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)', lineHeight: '1.4' }}>
                          {geographic_distribution.top_regions?.length > 0
                            ? geographic_distribution.top_regions.map((r: any) => r.name).join(', ').substring(0, 35) + (geographic_distribution.top_regions.map((r: any) => r.name).join(', ').length > 35 ? '...' : '')
                            : 'Nenhuma região'}
                        </p>
                      </motion.div>

                      {/* Segmentos de Clientes */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 }}
                        className="p-8 rounded-xl text-center"
                        style={{
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)'
                        }}
                      >
                        <div className="text-5xl font-bold mb-2 text-white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                          {client_segmentation.segments ? client_segmentation.segments.length :
                           client_segmentation.segment_distribution ? client_segmentation.segment_distribution.length : 0}
                        </div>
                        <div className="text-sm font-bold text-white mb-3" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Segmentos de Clientes</div>
                        <p className="text-xs text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)', lineHeight: '1.4' }}>
                          {client_segmentation.segments?.length > 0
                            ? client_segmentation.segments.map((s: any) => s.name || s).join(', ').substring(0, 35) + (client_segmentation.segments.map((s: any) => s.name || s).join(', ').length > 35 ? '...' : '')
                            : client_segmentation.segment_distribution?.length > 0
                            ? client_segmentation.segment_distribution.map((s: any) => s.segment || s.name || s).join(', ').substring(0, 35) + '...'
                            : 'Nenhum segmento'}
                        </p>
                      </motion.div>

                      {/* Custo Total de Frete */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className="p-8 rounded-xl text-center"
                        style={{
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          boxShadow: '0 4px 6px rgba(245, 158, 11, 0.3)'
                        }}
                      >
                        <div className="text-4xl font-bold mb-2 text-white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                          {formatCurrency(vehicle_analysis.total_freight_cost || 0)}
                        </div>
                        <div className="text-sm font-bold text-white mb-3" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Custo Total de Frete</div>
                        <p className="text-xs text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)', lineHeight: '1.4' }}>
                          {vehicle_analysis.vehicle_types?.length || 0} tipos de veículos
                        </p>
                      </motion.div>

                      {/* Cidades Cobertas */}
                      {geographic_distribution.covered_cities !== undefined && geographic_distribution.covered_cities > 0 ? (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3 }}
                          className="p-8 rounded-xl text-center"
                          style={{
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                            boxShadow: '0 4px 6px rgba(139, 92, 246, 0.3)'
                          }}
                        >
                          <div className="text-5xl font-bold mb-2 text-white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                            {geographic_distribution.covered_cities}
                          </div>
                          <div className="text-sm font-bold text-white mb-3" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Cidades Cobertas</div>
                          <p className="text-xs text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)', lineHeight: '1.4' }}>
                            Cobertura geográfica real
                          </p>
                        </motion.div>
                      ) : (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3 }}
                          className="p-8 rounded-xl text-center"
                          style={{
                            background: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
                            boxShadow: '0 4px 6px rgba(148, 163, 184, 0.3)'
                          }}
                        >
                          <div className="text-5xl font-bold mb-2 text-white" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                            -
                          </div>
                          <div className="text-sm font-bold text-white mb-3" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Cidades Cobertas</div>
                          <p className="text-xs text-white" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)', lineHeight: '1.4' }}>
                            Dados não disponíveis
                          </p>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>

                  {/* Distribuição de Produtos - Gráfico Pizza */}
                  {product_distribution.top_products && product_distribution.top_products.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-8 rounded-2xl shadow-lg"
                      style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                    >
                      <h3 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                        <Package className="w-6 h-6" style={{ color: '#10b981' }} />
                        Produtos Transportados
                      </h3>
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Pie
                            data={product_distribution.top_products.slice(0, 6).map((p: any) => ({
                              name: p.name,
                              value: p.volume || 0
                            }))}
                            cx="50%"
                            cy="50%"
                            outerRadius={selectedProductIndex !== undefined ? 140 : 90}
                            innerRadius={selectedProductIndex !== undefined ? 0 : 0}
                            fill="#10b981"
                            dataKey="value"
                            activeIndex={selectedProductIndex}
                            activeShape={{
                              filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.6))',
                              stroke: '#fff',
                              strokeWidth: 3,
                            } as any}
                          >
                            {['#10b981', '#3B82F6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'].map((color, index) => {
                              const isSelected = selectedProductIndex === index;
                              return (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={color}
                                  opacity={isSelected ? 1 : 0.6}
                                  stroke={isSelected ? '#fff' : 'none'}
                                  strokeWidth={isSelected ? 3 : 0}
                                />
                              );
                            })}
                          </Pie>
                          <Tooltip
                            formatter={(value: any) => formatNumber(value)}
                            contentStyle={{
                              backgroundColor: 'var(--bg-secondary)',
                              border: '1px solid var(--border-primary)',
                              borderRadius: '8px',
                              color: 'var(--text-primary)'
                            }}
                            cursor={{ fill: 'rgba(0,0,0,0.1)' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                        {product_distribution.top_products.slice(0, 6).map((p: any, idx: number) => {
                          const isSelected = selectedProductIndex === idx;
                          const productColor = ['#10b981', '#3B82F6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'][idx];

                          return (
                            <motion.div
                              key={idx}
                              onClick={() => setSelectedProductIndex(isSelected ? undefined : idx)}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-all duration-200"
                              style={{
                                backgroundColor: isSelected ? `${productColor}20` : 'var(--bg-secondary)',
                                border: `2px solid ${isSelected ? productColor : 'transparent'}`,
                              }}
                            >
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: productColor }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                                  {p.name}
                                </p>
                                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                  {formatNumber(p.volume)}
                                </p>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}

                  {/* Análise de Clientes por Região - Gráfico Barras */}
                  {geographic_distribution.top_regions && geographic_distribution.top_regions.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-8 rounded-2xl shadow-lg"
                      style={{ backgroundColor: 'var(--bg-card)', border: '2px solid var(--border-primary)' }}
                    >
                      <h3 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                        <Users className="w-6 h-6" style={{ color: '#8b5cf6' }} />
                        Clientes por Região
                      </h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={geographic_distribution.top_regions.slice(0, 8).map((r: any) => ({
                            name: r.name || r,
                            clientes: r.volume || 0
                          }))}
                          margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="var(--border-primary)"
                          />
                          <XAxis
                            dataKey="name"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                            tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                          />
                          <YAxis
                            tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                          />
                          <Tooltip
                            formatter={(value: any) => formatNumber(value)}
                            contentStyle={{
                              backgroundColor: 'var(--bg-secondary)',
                              border: '1px solid var(--border-primary)',
                              borderRadius: '8px',
                              color: 'var(--text-primary)'
                            }}
                          />
                          <Bar
                            dataKey="clientes"
                            fill="#8b5cf6"
                            radius={[8, 8, 0, 0]}
                            name="Clientes"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                        {geographic_distribution.top_regions.slice(0, 8).map((r: any, idx: number) => (
                          <div key={idx} className="p-3 rounded-lg text-center" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                            <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                              {formatNumber(r.volume || 0)}
                            </p>
                            <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                              {r.name || r}
                            </p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {/* Análise de Rotas */}
                  {vehicle_analysis.vehicle_types && vehicle_analysis.vehicle_types.length > 0 && (
                    <DetailCard
                      icon={Route}
                      title="Tipos de Veículos"
                      color="#f59e0b"
                      items={vehicle_analysis.vehicle_types.map((v: any) => ({
                        emoji: '🚚',
                        label: v.type,
                        value: formatNumber(v.count) + ' unidades'
                      }))}
                    />
                  )}

                  {/* Insights com IA */}
                  {insights && insights.length > 0 && (
                    <div>
                      <h3 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                        <Lightbulb className="w-6 h-6" style={{ color: '#f59e0b' }} />
                        Insights com IA
                      </h3>
                      <div className="space-y-3">
                        {insights.slice(0, 3).map((insight: any, idx: number) => {
                          const colors = ['#ef4444', '#f59e0b', '#10b981'];
                          const icons = [Zap, Target, TrendingUp];
                          const Icon = icons[idx % icons.length];

                          return (
                            <InsightCard
                              key={idx}
                              icon={Icon}
                              title={insight.title || `Insight ${idx + 1}`}
                              description={insight.description || insight}
                              color={colors[idx % colors.length]}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {!data || Object.keys(data).length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12">
                      <AlertCircle className="h-12 w-12 opacity-40 mb-4" />
                      <p className="text-center opacity-60">
                        Selecione um período para visualizar resumo da malha logística
                      </p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LogisticsNetworkModal;
